import { Component, type ReactNode } from "react";
import { throttle } from "underscore";

/**
 * This is an InfiniteScroll component. It wraps children that you want to infiniteScroll.
 *
 * @prop loadingData : Whenever you are loading data caused by an infinite scroll event. Prevents additional triggers.
 * @prop nodeId : The id of the node to listen for scrolling events.
 * @prop onInfiniteScroll : Function that is called when new data should be loaded to infinite scroll.
 * @prop limit : Number of new resources to load per infiniteScroll event.
 * @prop resourceCount : Number of resources already loaded.
 */

interface Props {
  resourceCount: number;
  limit: number;
  nodeId?: string;
  loadingData: boolean;
  onInfiniteScroll: (offset: number) => void;
  children: ReactNode;
}

interface State {
  offset: number;
  infiniteScrollInitialized: boolean;
  infiniteScrollCallback: () => void;
}

export class InfiniteScroll extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      offset: 0,
      infiniteScrollInitialized: false,
      infiniteScrollCallback: throttle(this.executeInfiniteScroll, 700),
    };
  }

  componentDidMount() {
    this.initializeInfiniteScroll();
  }

  // TODO: Test this, most likely will not work!
  componentDidUpdate(prevProps: Props, prevState: State) {
    const { infiniteScrollInitialized } = prevState;
    const { resourceCount } = this.props;

    if (this.stopInfiniteScrolling(resourceCount)) {
      this.setState({ offset: 0 });
      this.removeInfiniteScrollEventListener();
    } else if (Number.isInteger(resourceCount / prevProps.limit) && !infiniteScrollInitialized) {
      this.initializeInfiniteScroll();
    }
  }

  stopInfiniteScrolling = (resourceCount: number) => {
    const { infiniteScrollInitialized } = this.state;
    return (
      resourceCount &&
      !Number.isInteger(resourceCount / this.props.limit) &&
      infiniteScrollInitialized
    );
  };

  getInfiniteScrollNode = (): Document | HTMLElement | null => {
    const { nodeId } = this.props;

    if (nodeId) {
      return document.getElementById(nodeId);
    }

    return document;
  };

  initializeInfiniteScroll = () => {
    if (!this.state.infiniteScrollInitialized) {
      this.setState({ infiniteScrollInitialized: true }, () => {
        this.getInfiniteScrollNode()?.addEventListener("scroll", this.state.infiniteScrollCallback);
      });
    }
  };

  executeInfiniteScroll = () => {
    const { nodeId, loadingData, onInfiniteScroll, limit } = this.props;
    let infiniteScrollNode = document.body;
    let scrollTop =
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    if (nodeId) {
      infiniteScrollNode = document.getElementById(nodeId) || infiniteScrollNode;
      scrollTop = infiniteScrollNode.scrollTop;
    }

    if (scrollTop > infiniteScrollNode.scrollHeight - infiniteScrollNode.clientHeight * 4) {
      if (!loadingData) {
        this.setState({ offset: this.state.offset + limit }, () => {
          window.requestAnimationFrame(() => {
            onInfiniteScroll(this.state.offset);
          });
        });
      }
    }
  };

  removeInfiniteScrollEventListener = () => {
    if (this.state.infiniteScrollInitialized) {
      this.setState({ infiniteScrollInitialized: false });
      this.getInfiniteScrollNode()?.removeEventListener(
        "scroll",
        this.state.infiniteScrollCallback,
        false,
      );
    }
  };

  render() {
    return <div className="infinite-scroll-component">{this.props.children}</div>;
  }
}
